import { useState } from 'react'
import qs from 'qs'

import { sendToApi } from 'api/client'

import { useModals } from 'almanac-ui/Modal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateDesignSync } from './hooks/useCreateDesignSync'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

import { MultiStepWizard } from '../MultiStepWizard/MultiStepWizard'
import { VisualElement } from 'api/clients/VisualElementClient'
import { RecommendMoreContentSchema } from '../schemas/RecommendMoreContent'
import { ChooseDestination, Target } from './components/ChooseDestination/ChooseDestination'
import { Review } from './components/Review/Review'
import { AlmanacFile, AlmanacFileSummary } from 'types'
import { Plan } from './components/Plan/Plan'
import { reportError } from 'utils/error'
import { TurnIntoWizard } from '../TurnInto/TurnIntoWizard'
import { FolderClient } from 'api/clients/FolderClient'
import { useDesignChannelSubscription } from './hooks/useDesignChannelSubscription'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

export type Design = AlmanacFileSummary & { parent: { seo_string: string } }

export const RecommendMoreContentWizard = ({
  postId,
  topic,
  externalIntegrationId,
}: {
  postId: number
  topic: string
  externalIntegrationId: number
}) => {
  const workspaceId = useCurrentWorkspaceId()
  const [designs, setDesigns] = useState<(Design | undefined)[]>(new Array(5).fill(undefined))
  useDesignChannelSubscription({ designs, setDesigns })
  const [project, setProject] = useState<AlmanacFile>()
  const [savedPostAmount, setSavedPostAmount] = useState<number>(5)
  const [campaignId, setCampaignId] = useState()
  const [usedVisualElementIds, setUsedVisualElementIds] = useState<number[]>([])
  const { openModal, closeAllModals } = useModals()
  const { mutateAsync: createDesignMutation } = useCreateDesignSync()
  const { data } = useExternalIntegrations()
  const externalIntegration = data?.external_integrations.find(
    (integration) => integration.id === externalIntegrationId,
  )

  const chooseDestination = async (values: {
    filename: string
    post_amount: string
    project: AlmanacFile
    target: Target
  }) => {
    const { target, project, filename } = values
    const postAmount = parseInt(values.post_amount)
    setProject(project)
    setSavedPostAmount(postAmount)

    if (target === Target.Create) {
      if (postAmount !== 5) {
        setDesigns(new Array(postAmount).fill(undefined))
      }

      const templatesParams = qs.stringify({
        width: 1080,
        height: 1080,
        description: topic || '',
        items: postAmount,
      })

      const templatesResponse = await sendToApi<{ visual_elements: VisualElement[] }>(
        `/visual_elements/find_by_description?${templatesParams}`,
      )

      if (templatesResponse.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Unable to find templates to match your post')
      }

      setUsedVisualElementIds(templatesResponse.data.visual_elements.map((e) => e.id))

      const campaignResponse = await sendToApi(`/w/${workspaceId}/content_campaigns`, {
        method: 'POST',
        body: {
          name: filename,
          project_key: project.id,
        },
      })

      if (campaignResponse.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Error creating campaign')
      }

      setCampaignId(campaignResponse.data.content_campaign.id)

      const promises = templatesResponse.data.visual_elements
        .slice(0, postAmount)
        .map(async ({ id: sourceVisualElementId }) => {
          return createDesignMutation({
            workspaceId,
            projectId: project.id,
            postId,
            postAmount,
            campaignId: campaignResponse.data.content_campaign.id,
            sourceVisualElementId,
          })
            .then(async (data) => {
              if (data.almanac_file?.id) {
                const res = await sendToApi(`/almanac_files/${data.almanac_file.id}`)

                if (res.type !== SUCCESS_RESPONSE_TYPE) {
                  throw new Error('Error fetching design')
                }

                setDesigns((prev) => {
                  const existingDesigns = prev.filter((p) => !!p)
                  let undefinedDesigns = []
                  if (existingDesigns.length < 5) {
                    undefinedDesigns = new Array(4 - existingDesigns.length).fill(undefined)
                  }
                  return [...existingDesigns, res.data.almanac_file, ...undefinedDesigns]
                })
              }
            })
            .catch((error) => {
              showToastMessage('Design generation failed', 'error')
              reportError(error)
              throw new Error('Error creating design')
            })
        })

      await Promise.all(promises)

      return
    }

    openModal(
      TurnIntoWizard,
      {
        fromDocProps: {
          documentAsMarkdown: topic,
        },
        initialIsCampaign: true,
        initialValues: {
          filename,
          project,
        },
      },
      {
        replace: true,
      },
    )
  }

  const regenerate = async (index: number) => {
    const designToRegenerate = designs[index]

    if (!campaignId || !project || !designToRegenerate) return

    const templatesParams = qs.stringify({
      width: 1080,
      height: 1080,
      description: topic || '',
      items: usedVisualElementIds.length + 5,
    })

    const templatesResponse = await sendToApi<{ visual_elements: VisualElement[] }>(
      `/visual_elements/find_by_description?${templatesParams}`,
    )

    if (templatesResponse.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error('Unable to find templates to match your post')
    }

    const sourceVisualElement = templatesResponse.data.visual_elements.filter(
      (vE) => !usedVisualElementIds.includes(vE.id),
    )[0]

    if (!sourceVisualElement) return

    setUsedVisualElementIds([...usedVisualElementIds, sourceVisualElement.id])
    setDesigns((prev) => {
      return prev.map((d, i) => {
        if (i === index) return undefined

        return d
      })
    })

    FolderClient.bulkDestroyFiles([], [], [designToRegenerate.id])

    createDesignMutation({
      workspaceId,
      projectId: project.id,
      postId,
      postAmount: 1,
      campaignId,
      sourceVisualElementId: sourceVisualElement.id,
    })
      .then(async (data) => {
        if (data.almanac_file?.id) {
          const res = await sendToApi(`/almanac_files/${data.almanac_file.id}`)

          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            throw new Error('Error fetching design preview')
          }

          setDesigns((prev) => {
            return prev.map((design, i) => {
              if (i === index) return res.data.almanac_file

              return design
            })
          })
        }
      })
      .catch((error) => {
        reportError(error)
        throw new Error('Error creating design')
      })
  }

  const deleteDesigns = () => {
    const almanacFileIds = (designs.filter((d) => !!d) as Design[]).map((d) => d.id)

    if (almanacFileIds.length === 0) return

    setDesigns(new Array(savedPostAmount).fill(undefined))
    FolderClient.bulkDestroyFiles([], [], almanacFileIds)
  }

  const deleteDesign = (index: number) => {
    const almanacFileId = designs[index]?.id

    if (!almanacFileId) return

    setDesigns((prev) => {
      return prev.filter((d, i) => i !== index)
    })

    FolderClient.bulkDestroyFiles([], [], [almanacFileId])
  }

  return (
    <MultiStepWizard.Root schema={RecommendMoreContentSchema} onSubmit={closeAllModals} onClose={deleteDesigns}>
      {externalIntegration && (
        <ChooseDestination
          chooseDestination={chooseDestination}
          externalIntegration={externalIntegration}
          topic={topic}
        />
      )}
      <Review
        designs={designs}
        externalIntegration={externalIntegration}
        regenerate={regenerate}
        deleteDesign={deleteDesign}
        deleteDesigns={deleteDesigns}
      />
      {externalIntegration && project && (
        <Plan
          designs={designs}
          integrationName={integrationToName[externalIntegration?.provider]}
          project={project}
          deleteDesign={deleteDesign}
        />
      )}
    </MultiStepWizard.Root>
  )
}
